import React, { useEffect, useState } from 'react';
import { cx } from 'linaria';
import { SelectionGroup, SelectionGroupItem } from 'fronton-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { charSets } from 'utils/constants';
import { analyticsUtils } from 'utils';
import { charGroupContainer, inactiveFilter } from './styles';
import { setBrandsFilter } from './ducks/brands-filter-slice';
import { getBrandsFilter } from './ducks/selectors';
import { getActiveGroups, getBrandsKeys } from '../all-brands/ducks/get-brands';

type Props = {
  scrollCallback: () => void;
};

export const BrandsFilter: React.FC<Props> = ({ scrollCallback }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [activeGroup = 'all', activeChar] = useSelector(getBrandsFilter);
  const [group, setGroup] = useState(activeGroup);
  const brandsKeys = useSelector(getBrandsKeys);
  const activeGroups = useSelector(getActiveGroups);

  const labels: Record<string, string> = {
    latin: t('ui.filter.latin'),
    cyrillic: t('ui.filter.regional'),
    numbers: t('ui.filter.numbers'),
    all: t('ui.filter.all'),
  };

  useEffect(() => {
    setGroup(activeGroup);
  }, [activeGroup]);

  const sendGaClickButton = (handlerType: 'group' | 'char', filterLabel: string) => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'brandListFilter', // блок, в котором расположен элемент
      elementName: t('allBrands'), // название блока
      elementText: filterLabel, // текст выбранного элемента
      elementContext: handlerType === 'group' ? 'level1' : 'level2', // уровень фильтра (level2 для элементов A, B, C, etc.)
    });
  };

  const handleGroupPick = (value: string) => {
    const groupLabel = labels[value];
    sendGaClickButton('group', groupLabel);
    setGroup(value);
    dispatch(setBrandsFilter([value, (value === 'numbers' ? '0-9' : null) as string]));
    scrollCallback();
  };

  const handleCharPick = (value: string) => {
    sendGaClickButton('char', value);
    dispatch(setBrandsFilter([group as string, value]));
    scrollCallback();
  };

  return (
    <>
      <SelectionGroup
        active={activeGroup || 'all'}
        onChange={(event, value) => handleGroupPick(value as string)}
        size="s"
        data-qa="group-filter"
        variant="pseudo"
      >
        <SelectionGroupItem value="all" data-qa="filter-group-all">
          {t('ui.filter.all')}
        </SelectionGroupItem>
        <SelectionGroupItem
          value="latin"
          data-qa="filter-group-latin"
          className={activeGroups.latin ? undefined : inactiveFilter}
        >
          {t('ui.filter.latin')}
        </SelectionGroupItem>
        <SelectionGroupItem
          value="cyrillic"
          data-qa="filter-group-cyrillic"
          className={activeGroups.cyrillic ? undefined : inactiveFilter}
        >
          {t('ui.filter.regional')}
        </SelectionGroupItem>
        <SelectionGroupItem
          value="numbers"
          data-qa="filter-group-numbers"
          className={activeGroups.numbers ? undefined : inactiveFilter}
        >
          {t('ui.filter.numbers')}
        </SelectionGroupItem>
      </SelectionGroup>
      {!!activeGroup && ['cyrillic', 'latin'].includes(activeGroup) ? (
        <div data-qa="character-filter">
          <SelectionGroup
            className={cx(charGroupContainer)}
            active={activeChar || ''}
            onChange={(event, value) => handleCharPick(value as string)}
            size="s"
            variant="pseudo"
          >
            {charSets[activeGroup as keyof typeof charSets].split('').map((c: string) => (
              <SelectionGroupItem
                key={c}
                value={c}
                className={brandsKeys.includes(c) ? undefined : inactiveFilter}
              >
                {c}
              </SelectionGroupItem>
            ))}
          </SelectionGroup>
        </div>
      ) : null}
    </>
  );
};
